import { err, ok, type Result as NTResult } from 'neverthrow';
import { z } from 'zod';

import { zeroPadLeft } from '../pad.js';
import { Timestamp } from './timestamp.js';
import { YearMonth } from './year_month.js';

const separator = '-';

declare const naiveDateBrand: unique symbol;

/**
 * Branded string type for a calendar date without time or timezone.
 *
 * The canonical format is `"YYYY-MM-DD"` (ISO 8601 extended). Operations live on
 * the `NaiveDate` namespace value below; the branded primitive is naturally
 * usable as a `Map`/`Set` key and sorts chronologically under plain string
 * comparison.
 */
export type NaiveDate = string & { readonly [naiveDateBrand]: 'NaiveDate' };

const NAIVE_DATE_PATTERN = /^(\d{4})-?(\d{2})-?(\d{2})$/;

function format(year: number, month: number, date: number): NaiveDate {
  return `${zeroPadLeft(year, 4)}${separator}${zeroPadLeft(month, 2)}${separator}${zeroPadLeft(
    date,
    2,
  )}` as NaiveDate;
}

export const NaiveDate = {
  // ----- Smart constructors -----

  /**
   * Parse and validate a string in `"YYYY-MM-DD"` or `"YYYYMMDD"` form,
   * normalizing to the canonical `"YYYY-MM-DD"`. Use this for values coming from
   * outside the type-safe layer (network, URL, user input).
   */
  parse(value: string): NTResult<NaiveDate, string> {
    const match = NAIVE_DATE_PATTERN.exec(value);
    if (match === null) {
      return err(`Invalid NaiveDate string (expected "YYYY-MM-DD"): ${value}`);
    }
    const month = Number(match[2]);
    const date = Number(match[3]);
    if (month < 1 || month > 12) {
      return err(`Invalid NaiveDate: month out of range in ${value}`);
    }
    if (date < 1 || date > 31) {
      return err(`Invalid NaiveDate: day out of range in ${value}`);
    }
    return ok(`${match[1]}${separator}${match[2]}${separator}${match[3]}` as NaiveDate);
  },

  /**
   * Cast a string to `NaiveDate` without validation. Use only for values that
   * are provably already in the canonical `"YYYY-MM-DD"` format.
   */
  unsafeFromString(value: string): NaiveDate {
    return value as NaiveDate;
  },

  /**
   * Type guard for a valid canonical `"YYYY-MM-DD"` value: the extended shape
   * plus in-range month and day. Backs the `schema`/`decoder` boundary checks;
   * rejects the basic `"YYYYMMDD"` form (use `fromIsoString`/`parse` for that).
   */
  isNaiveDate(value: string): value is NaiveDate {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return false;
    }
    const month = Number(value.slice(5, 7));
    const date = Number(value.slice(8, 10));
    return month >= 1 && month <= 12 && date >= 1 && date <= 31;
  },

  /** Accepts extended (`YYYY-MM-DD`) or basic (`YYYYMMDD`) ISO form; throws on invalid input. */
  fromIsoString(value: string): NaiveDate {
    const result = NaiveDate.parse(value);
    if (result.isErr()) {
      throw new Error(`No formats matched given date string. Received: ${value}`);
    }
    return result.value;
  },

  /**
   * Constructs a naive date from a calendar-date-like value, e.g.
   * `@internationalized/date`'s `CalendarDate` (structural, so that library is
   * not a dependency here).
   */
  fromCalendarDate(value: { year: number; month: number; day: number }): NaiveDate {
    return format(value.year, value.month, value.day);
  },

  /** Constructs a naive date from a JS `Date`, accounting for the local timezone offset. */
  fromDate(value: Date): NaiveDate {
    const offsetMinutes = value.getTimezoneOffset();
    return NaiveDate.fromUnixTimeMilliseconds(value.getTime() - offsetMinutes * 60 * 1000);
  },

  /** The local calendar date of a `Timestamp`. */
  fromTimestamp(value: Timestamp): NaiveDate {
    return NaiveDate.fromDate(Timestamp.toJsDate(value));
  },

  fromUnixTimeMilliseconds(unixTime: number): NaiveDate {
    return NaiveDate.unsafeFromString(new Date(unixTime).toISOString().slice(0, 10));
  },

  fromUnixTimeSeconds(unixTime: number): NaiveDate {
    return NaiveDate.fromUnixTimeMilliseconds(unixTime * 1000);
  },

  fromYearMonth(yearMonth: YearMonth): NaiveDate {
    return NaiveDate.fromDate(
      new Date(YearMonth.year(yearMonth), YearMonth.monthNumber(yearMonth) - 1),
    );
  },

  now(): NaiveDate {
    return NaiveDate.fromUnixTimeSeconds(Date.now() / 1000);
  },

  // ----- Field accessors -----

  year(value: NaiveDate): number {
    return Number(value.slice(0, 4));
  },

  month(value: NaiveDate): number {
    return Number(value.slice(5, 7));
  },

  date(value: NaiveDate): number {
    return Number(value.slice(8, 10));
  },

  isLeapYear(value: NaiveDate): boolean {
    const year = NaiveDate.year(value);
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  },

  toDate(value: NaiveDate): Date {
    return new Date(NaiveDate.year(value), NaiveDate.month(value) - 1, NaiveDate.date(value));
  },

  // ----- Comparisons -----

  /** Chronological ordering; the canonical format makes this a string comparison. */
  compare(a: NaiveDate, b: NaiveDate): number {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  },

  /** Number of whole days (nights) between the two dates. */
  dateDiffInDays(value: NaiveDate, other: NaiveDate): number {
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const start = NaiveDate.toDate(value);
    const end = NaiveDate.toDate(other);
    const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.floor(Math.abs(utcEnd - utcStart) / millisecondsPerDay);
  },

  // ----- Date arithmetic -----

  /** The date `n` days before `value` (default 1). */
  previous(value: NaiveDate, n: number = 1): NaiveDate {
    if (n < 1) {
      throw new Error('n must be greater than 0!');
    }
    const date = NaiveDate.toDate(value);
    date.setDate(date.getDate() - n);
    return NaiveDate.fromDate(date);
  },

  /** The date `n` days after `value` (default 1). */
  next(value: NaiveDate, n: number = 1): NaiveDate {
    if (n < 1) {
      throw new Error('n must be greater than 0!');
    }
    const date = NaiveDate.toDate(value);
    date.setDate(date.getDate() + n);
    return NaiveDate.fromDate(date);
  },

  /** Shift the year by `increment` (may be negative). */
  updateYear(value: NaiveDate, increment: number): NaiveDate {
    const date = NaiveDate.toDate(value);
    date.setFullYear(date.getFullYear() + increment);
    return NaiveDate.fromDate(date);
  },

  // ----- Display -----

  /** ISO 8601 basic calendar date (`YYYYMMDD`, no separators). */
  toIsoStringBasic(value: NaiveDate): string {
    return `${value.slice(0, 4)}${value.slice(5, 7)}${value.slice(8, 10)}`;
  },

  display(value: NaiveDate): string {
    return value;
  },

  // ----- Coders (cf. simple_id.ts) -----

  /**
   * Zod schema for the canonical `"YYYY-MM-DD"` form: validates and narrows the
   * input string to `NaiveDate` without reallocating it. Rejects the basic
   * `"YYYYMMDD"` form — use `fromIsoString`/`parse` for inputs that need it.
   */
  schema: z.string().refine((value): value is NaiveDate => NaiveDate.isNaiveDate(value), {
    message: 'Invalid NaiveDate (expected "YYYY-MM-DD")',
  }),
};

export { format as unsafeFormatNaiveDate };
